/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React from 'react';
import { Image, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { pd } from '@airasia-rn/ui';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import ButtonView, {
  ButtonViewType,
} from '@airasia-phoenix/react-native/components/ButtonView';
import { getTextStyle } from '@airasia-phoenix/react-native/utils';
import type { EmptyStateProps } from './types';

export const EmptyStateView = ({
  image,
  imageUrl,
  title,
  description,
  cta,
  ctaClicked,
}: EmptyStateProps) => {
  const imageSource = imageUrl ? { uri: imageUrl } : image;

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        {imageSource ? (
          <View style={styles.illustration}>
            <Image
              source={imageSource}
              resizeMode="contain"
              style={[styles.image]}
            />
          </View>
        ) : null}
        <View style={styles.title}>
          <TextView
            text={title}
            style={[
              getTextStyle(
                pd.sys.typescale.body_text_medium,
                pd.sys.colour.content_primary,
              ),
              styles.titleText,
            ]}
          />
        </View>

        {description && description.trim() !== '' && (
          <View style={styles.description}>
            <TextView
              text={description ?? ''}
              style={[
                getTextStyle(
                  pd.sys.typescale.sub_body_text,
                  pd.sys.colour.content_secondary,
                ),
                styles.descriptionText,
              ]}
            />
          </View>
        )}

        {cta && cta.trim() !== '' && (
          <View style={styles.ctaContainer}>
            <ButtonView
              type={ButtonViewType.SECONDARY_SMALL}
              text={cta}
              onPress={ctaClicked || (() => {})}
              disabled={false}
            />
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create(() => ({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
  },
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  illustration: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  image: {
    width: 303,
    height: 192,
  },
  titleText: {
    lineHeight: 24,
    textAlign: 'center',
  },
  title: {
    marginTop: 16,
  },
  descriptionText: {
    textAlign: 'center',
    lineHeight: 18,
  },
  description: {
    paddingHorizontal: 24,
    marginTop: 4,
    alignItems: 'center',
  },
  ctaContainer: {
    marginTop: 16,
    alignItems: 'center',
  },
}));
